import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Alert, Box, CircularProgress, Typography } from "@mui/material";
import { groups } from "../actprep-ui/constants/auth";
import { useProfile } from "../actprep-ui/context/ProfileContext";
import { useImpersonation } from "../actprep-ui/context/ImpersonationContext";
import { useGeneralAPI } from "../actprep-ui/hooks/api";
import { urls } from "../actprep-ui/utils/urls";

function buildParentImpersonation(parentData) {
  const firstName = parentData.user?.first_name || parentData.user__first_name || parentData.first_name;
  const lastName = parentData.user?.last_name || parentData.user__last_name || parentData.last_name;
  const email = parentData.user?.email || parentData.user__email || parentData.email;
  const fallbackLabel = `${firstName || ""} ${lastName || ""}`.trim();

  return {
    uuid: parentData.uuid,
    label: parentData.__str__ || fallbackLabel || email || "Unknown Parent",
    data: {
      user_profile_id:
        parentData.user?.profile?.uuid || parentData.user__profile__uuid,
      user: {
        first_name: firstName || "",
        last_name: lastName || "",
        email: email || "",
      },
    },
  };
}

export default function ImpersonateParent() {
  const { parentUuid } = useParams();
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { setImpersonation, clearImpersonation } = useImpersonation();
  const [error, setError] = useState(null);

  const isEmployee = profile?.groups?.includes(groups.EMPLOYEES);

  // Drop any prior target immediately so a same-tab switch cannot keep showing
  // the previous parent while this page loads the new one.
  useEffect(() => {
    clearImpersonation();
  }, [parentUuid, clearImpersonation]);

  const {
    data: parentResponse,
    isLoading,
    isError,
  } = useGeneralAPI(
    "ParentProfile",
    {
      filter: { uuid: parentUuid },
      values: [
        "uuid",
        "user__profile__uuid",
        "user__first_name",
        "user__last_name",
        "user__email",
        "first_name",
        "last_name",
        "email",
      ],
      keepRelated: true,
      selectRelated: ["user", "user__profile"],
      customSerializer: true,
    },
    {
      enabled: !!parentUuid && !!profile?.uuid && isEmployee,
    }
  );

  useEffect(() => {
    if (profile?.uuid && !isEmployee) {
      setError("Access Denied.");
      setTimeout(() => navigate(urls.error(403)), 2000);
      return;
    }

    if (parentResponse?.data?.length > 0) {
      const parentData = parentResponse.data[0];
      setImpersonation(buildParentImpersonation(parentData));

      navigate("/dashboard/");
    } else if (!isLoading && parentResponse && parentResponse.data?.length === 0) {
      setError("Parent not found.");
      setTimeout(() => navigate("/dashboard/"), 2000);
    } else if (isError) {
      setError("Failed to set up impersonation. Please try again.");
      setTimeout(() => navigate("/dashboard/"), 2000);
    }
  }, [parentResponse, isLoading, isError, profile, isEmployee, setImpersonation, navigate]);

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        minHeight: "50vh",
        gap: 2,
        padding: 4,
      }}
    >
      {isLoading && !error && (
        <>
          <CircularProgress />
          <Typography variant="h6" color="text.secondary">
            Setting up impersonation...
          </Typography>
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ maxWidth: 500 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
}
